import type { Range } from '@tiptap/core';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { SearchPicker } from '@/components/search/SearchPicker.web';
import { SidebarPageBlankIcon } from '@/components/layout/SidebarIcons.web';
import { normalizeExternalUrl, pageHref } from '@/lib/blocks/page-link-search';
import { useBlockNoteEditor, useComponentsContext } from '@blocknote/react';

type CouplespaceEditLinkMenuItemsProps = {
  url: string;
  text: string;
  range: Range;
  showTextField?: boolean;
  setToolbarOpen?: (open: boolean) => void;
  setToolbarPositionFrozen?: (frozen: boolean) => void;
  onClose?: () => void;
};

export function CouplespaceEditLinkMenuItems({
  url,
  text,
  showTextField = false,
  setToolbarOpen,
  setToolbarPositionFrozen,
  onClose,
}: CouplespaceEditLinkMenuItemsProps) {
  const Components = useComponentsContext();
  const editor = useBlockNoteEditor();
  const [query, setQuery] = useState(url);
  const [title, setTitle] = useState(text);

  useEffect(() => {
    setQuery(url);
    setTitle(text);
  }, [text, url]);

  const applyLink = useCallback(
    (href: string, linkText?: string) => {
      const normalized = normalizeExternalUrl(href);
      if (!normalized) {
        return;
      }
      editor.createLink(normalized, linkText || undefined);
      setToolbarOpen?.(false);
      setToolbarPositionFrozen?.(false);
      onClose?.();
    },
    [editor, onClose, setToolbarOpen, setToolbarPositionFrozen],
  );

  const onSubmit = useCallback(() => {
    applyLink(query, title || undefined);
  }, [applyLink, query, title]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
        event.preventDefault();
        onSubmit();
      }
    },
    [onSubmit],
  );

  if (!Components) {
    return null;
  }

  const Form = Components.Generic.Form;

  return (
    <div className="couplespace-link-picker">
      <Form.Root>
        <Form.TextInput
          autoFocus
          className="bn-text-input"
          icon={<SidebarPageBlankIcon />}
          name="url"
          onChange={(event: ChangeEvent<HTMLInputElement>) => setQuery(event.currentTarget.value)}
          onKeyDown={onKeyDown}
          onSubmit={onSubmit}
          placeholder="Paste link or search pages"
          value={query}
        />
        {showTextField ? (
          <Form.TextInput
            className="bn-text-input"
            icon={<SidebarPageBlankIcon />}
            name="title"
            onChange={(event: ChangeEvent<HTMLInputElement>) => setTitle(event.currentTarget.value)}
            onKeyDown={onKeyDown}
            onSubmit={onSubmit}
            placeholder="Link text"
            value={title}
          />
        ) : null}
      </Form.Root>

      <SearchPicker
        query={query}
        selectedIndex={0}
        onSelect={(item) =>
          applyLink(
            pageHref({ id: item.containerId, type: item.containerType }),
            title || item.title,
          )
        }
      />
    </div>
  );
}
